"use client"

import React, { useState, useEffect } from "react"
import {
  Table,
  Input,
  Select,
  Tag,
  Avatar,
  Button,
  Dropdown,
  Space,
  Spin,
  message,
  ConfigProvider,
} from "antd"
import {
  SearchOutlined,
  EyeOutlined,
  PlusOutlined,
  MoreOutlined,
  DownloadOutlined,
  DownOutlined,
} from "@ant-design/icons"
import { Analytics } from "../../Apis/analyticsApi"
import UserDashboard from "./UserView"
import { useSearchParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { jwtDecode } from "jwt-decode"
import { getUserCookies } from "../../utils/Cookies"
import {
  canCreateDashboardUser,
  canDeleteInModule,
} from "../../utils/modulePermissionSidebar"
import { translations } from "../../utils/Translation/staticTranslation"
import DeleteConfirmationModal from "../Common/Modals/DeleteConfirmationModal"
import CreateDashboardUserModal from "./CreateDashboardUserModal"
import UpdateDashboardUserPlanModal from "./UpdateDashboardUserPlanModal"
import { getStoreApiErrorMessage } from "../../helper/storeApiErrorMessage"

const { Option } = Select

/** Frontend column → backend `ordering` field */
const SORTABLE_COLUMNS = {
  country: "country",
  loginCount: "login_count",
  subscriptions: "total_subscriptions",
  lastSubscriptionDate: "last_subscription_start",
  spendings: "total_revenue_spend",
  zoryPaid: "zory_paid",
  lastActivity: "last_activity",
  createdOn: "created_at",
}

const DEFAULT_SORT_CONFIG = { key: "last_activity", direction: "desc" }

function getOrderingFromSortConfig(sortConfig) {
  if (!sortConfig?.key) return `-${DEFAULT_SORT_CONFIG.key}`
  return sortConfig.direction === "desc"
    ? `-${sortConfig.key}`
    : sortConfig.key
}

function hasActivePlan(plan) {
  const value = String(plan ?? "").trim()
  if (!value || value === "—") return false

  const lower = value.toLowerCase()
  if (lower === "none" || lower === "no plan" || lower === "no plans") {
    return false
  }

  if (value.includes("لا توجد باقة") || value.includes("بدون باقة")) {
    return false
  }

  return true
}

// 🌍 Country name formatter supporting Arabic and English
const getCountryName = (code, lang = "en") => {
  if (!code) return lang === "ar" ? "غير معروف" : "Unknown"
  try {
    const formatter = new Intl.DisplayNames([lang === "ar" ? "ar" : "en"], {
      type: "region",
    })
    return formatter.of(code.toUpperCase()) || code.toUpperCase()
  } catch {
    return code.toUpperCase()
  }
}

// 📅 Show ISO timestamps as plain YYYY-MM-DD; leave already-formatted strings as-is.
const formatDateOnly = (value) => {
  if (!value) return null
  const raw = String(value).trim()
  if (!raw || raw === "—" || raw.toLowerCase() === "none") return null
  const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/)
  return iso ? iso[1] : raw
}

const UserManagmentTable = () => {
  const lang = localStorage.getItem("language") || "ar"
  const commonErrorMsg = translations[lang]?.common_error_msg || {}
  const modal = translations[lang]?.modals
  const allUsersT = translations[lang]?.allUsersPage || {}
  const profileUser = useSelector((state) => state.user.user)
  const token = getUserCookies()?.token
  const decoded = token ? jwtDecode(token) : null
  const canCreateUser = canCreateDashboardUser(profileUser, decoded)
  // `edit_only` roles may change a user but never remove one.
  const canDeleteUser = canDeleteInModule(profileUser, "all-users", decoded)
  const [searchParams, setSearchParams] = useSearchParams()
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false)

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [availableCountries, setAvailableCountries] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [filters, setFilters] = useState({
    plan: [],
    country: "",
    status: "all",
  })
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT_CONFIG)
  const [deletingUserId, setDeletingUserId] = useState(null)
  const [userToDelete, setUserToDelete] = useState(null)
  const [updatePlanModalOpen, setUpdatePlanModalOpen] = useState(false)
  const [updatePlanUser, setUpdatePlanUser] = useState(null)

  // ✨ NEW: State for viewing user details - Initialize from URL
  const userIdFromUrl = searchParams.get("userId")
  const [selectedUserId, setSelectedUserId] = useState(userIdFromUrl || null)
  const [showUserDashboard, setShowUserDashboard] = useState(!!userIdFromUrl)

  // ⏳ Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 600)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // 🧠 Fetch users
  const fetchUsers = async (page = 1) => {
    setLoading(true)
    try {
      // Format plan filter: if array, join with comma or handle "all_paid_plans"
      let planFilter = ""
      if (Array.isArray(filters.plan) && filters.plan.length > 0) {
        if (filters.plan.includes("all_paid_plans")) {
          planFilter = "all_paid_plans"
        } else {
          // Join multiple plans with comma
          planFilter = filters.plan.join(",")
        }
      }

      const res = await Analytics.getUserList({
        search: debouncedSearch,
        status: filters.status || "all",
        plan: planFilter,
        country: filters.country,
        ordering: getOrderingFromSortConfig(sortConfig),
        page,
      })

      const responseData = res?.data || res
      const results = responseData?.results || []

      // ✅ Extract available countries dynamically
      if (responseData?.available_countries?.length) {
        const unique = [
          ...new Set(
            responseData.available_countries.map((c) => c.toUpperCase())
          ),
        ]
        setAvailableCountries(unique)
      }

      // ✅ Format user data
      const formatted = results.map((user) => {
        const code = user.country ? user.country.toUpperCase() : null
        return {
          key: user.user_id,
          userId: user.user_id, // ✨ Keep userId for navigation
          name: user.name || "—",
          email: user.email || "—",
          phone:
            user.phone_no ||
            user.phone ||
            user.phone_number ||
            user.mobile_number ||
            "—",
          country: getCountryName(code, lang), // Show in selected language
          countryCode: code,
          plan: lang === "ar" ? user.plan_arabic : user.active_plan || "None",
          planEn: user.active_plan || null,
          planAr: user.plan_arabic || null,
          planId: user.plan_id ?? user.active_plan_id ?? null,
          paymentMethod: user.current_payment_method || null,
          // Card brand behind the payment (VISA / MASTER / MADA / …).
          cardType:
            user.current_card_type ??
            user.card_type ??
            user.current_card_brand ??
            user.card_brand ??
            null,
          loginCount: user.login_count ?? 0,
          subscriptions: user.total_subscriptions || 0,
          // Date the most recent subscription started, plus whether that
          // subscription was the user's first one or a renewal.
          lastSubscriptionDate:
            user.last_subscription_date ??
            user.last_subscription_at ??
            user.latest_subscription_date ??
            user.last_subscription_start ??
            null,
          lastSubscriptionIsRenewal:
            user.last_subscription_is_renewal ??
            user.is_renewal_subscription ??
            (user.total_subscriptions > 1 ? true : user.total_subscriptions ? false : null),
          spendings: `SAR ${user.total_revenue_spend || 0}`,
          // Boolean: true = subscription paid internally by Zory, false = a
          // real paying user. Backend key: `zory_paid`.
          zory_paid: user.zory_paid ?? null,
          lastActivity:
            lang === "ar"
              ? user.last_activity_arabic || "—"
              : user.last_activity || "—",
          createdOn: user.joined_at || "None",
        }
      })

      setData(formatted)
      setPagination({
        current: page,
        pageSize: 10,
        total: responseData?.count || 0,
      })
    } catch (err) {
      console.error("❌ Error fetching users:", err)
    } finally {
      setLoading(false)
    }
  }

  // ✅ Re-fetch when filters/search/sort change
  useEffect(() => {
    fetchUsers(1)
    setPagination((prev) => ({ ...prev, current: 1 }))
  }, [debouncedSearch, filters, sortConfig])

  // ✅ Restore user view from URL when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const userIdFromUrl = searchParams.get("userId")

    if (userIdFromUrl) {
      // URL has userId - show user view if not already showing this user
      const userIdStr = selectedUserId?.toString()
      if (userIdFromUrl !== userIdStr) {
        setSelectedUserId(userIdFromUrl)
        setShowUserDashboard(true)
      }
    } else {
      // URL has no userId - hide user view if it's showing
      if (showUserDashboard) {
        setShowUserDashboard(false)
        setSelectedUserId(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const handleTableChange = (paginationInfo) => {
    fetchUsers(paginationInfo?.current || 1)
  }

  const handleSort = (columnKey) => {
    const sortKey = SORTABLE_COLUMNS[columnKey]
    if (!sortKey) return

    setSortConfig((current) =>
      current?.key === sortKey
        ? {
            key: sortKey,
            direction: current.direction === "asc" ? "desc" : "asc",
          }
        : { key: sortKey, direction: "asc" }
    )
  }

  const renderSortableTitle = (label, columnKey) => {
    const sortKey = SORTABLE_COLUMNS[columnKey]
    const isActive = sortConfig?.key === sortKey
    const sortIcon = isActive
      ? sortConfig.direction === "asc"
        ? "▲"
        : "▼"
      : "▼"

    return (
      <span
        className="sortable-header"
        onClick={() => handleSort(columnKey)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          whiteSpace: "nowrap",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        {label}
        <span
          className="sort-indicator"
          style={{
            flexShrink: 0,
            fontSize: 10,
            lineHeight: 1,
            color: isActive ? "#344054" : "#98A2B3",
          }}
        >
          {sortIcon}
        </span>
      </span>
    )
  }

  const nowrapHeader = (label) => (
    <span style={{ whiteSpace: "nowrap" }}>{label}</span>
  )

  const handleFilterChange = (type, value) => {
    setFilters((prev) => ({
      ...prev,
      [type]:
        value === type
          ? type === "plan"
            ? []
            : type === "status"
              ? "all"
              : ""
          : value,
    }))
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  // Handle plan filter change with special logic for "Paid Plans" and "All Plans"
  const handlePlanFilterChange = (selectedPlans) => {
    // Check if "plan" (All Plans) was just selected (it's in new selection but wasn't in previous)
    const wasAllPlansSelected = selectedPlans.includes("plan")

    if (wasAllPlansSelected) {
      setFilters((prev) => ({
        ...prev,
        plan: [],
      }))
    }
    // If "all_paid_plans" is in the selection, only keep that option (exclusive)
    else if (selectedPlans.includes("all_paid_plans")) {
      setFilters((prev) => ({
        ...prev,
        plan: ["all_paid_plans"],
      }))
    } else {
      // If individual plans are selected, remove "all_paid_plans" and "plan" if they exist
      const filteredPlans = selectedPlans.filter((p) => p !== "plan" && p !== "all_paid_plans")
      setFilters((prev) => ({
        ...prev,
        plan: filteredPlans.length > 0 ? filteredPlans : [],
      }))
    }
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const handleExportCSV = async () => {
    try {
      setLoading(true)
      // Format plan filter for export
      let planFilterExport = ""
      if (Array.isArray(filters.plan) && filters.plan.length > 0) {
        if (filters.plan.includes("all_paid_plans")) {
          planFilterExport = "all_paid_plans"
        } else {
          planFilterExport = filters.plan.join(",")
        }
      }

      const res = await Analytics.getUserList({
        search: debouncedSearch,
        status: filters.status || "all",
        plan: planFilterExport,
        country: filters.country,
        ordering: getOrderingFromSortConfig(sortConfig),
        page: pagination.current,
        exportType: "csv",
      })

      // 🧾 Handle CSV file download
      if (res?.data) {
        if (typeof res.data === "string" && res.data.startsWith("http")) {
          // 🔗 If backend gives a file URL
          window.open(res.data, "_blank")
        } else {
          // 📄 Direct CSV content
          const blob = new Blob([res.data], {
            type: "text/csv;charset=utf-8;",
          })
          const link = document.createElement("a")
          const url = URL.createObjectURL(blob)
          link.href = url
          link.setAttribute(
            "download",
            `users-list-${new Date().toISOString().split("T")[0]}.csv`
          )
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
        }
      }
    } catch (err) {
      console.error("❌ Error exporting CSV:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenDeleteModal = (record) => {
    setUserToDelete({ userId: record.userId, name: record.name })
  }

  const handleOpenUpdatePlanModal = (record) => {
    setUpdatePlanUser(record)
    setUpdatePlanModalOpen(true)
  }

  const handleCloseUpdatePlanModal = () => {
    setUpdatePlanModalOpen(false)
    setUpdatePlanUser(null)
  }

  const handleCloseDeleteModal = () => {
    if (deletingUserId) return
    setUserToDelete(null)
  }

  const handleConfirmDelete = async () => {
    const userId = userToDelete?.userId
    if (!userId) return
    setDeletingUserId(userId)
    try {
      await Analytics.deleteUser(userId)
      message.success(
        commonErrorMsg.user_deleted_successfully ||
          (lang === "ar"
            ? "تم حذف المستخدم بنجاح"
            : "User deleted successfully")
      )
      setUserToDelete(null)

      const nextTotal = Math.max((pagination.total || 1) - 1, 0)
      const maxPageAfterDelete = Math.max(
        1,
        Math.ceil(nextTotal / pagination.pageSize)
      )
      const targetPage = Math.min(pagination.current, maxPageAfterDelete)
      fetchUsers(targetPage)
    } catch (error) {
      const fallback =
        commonErrorMsg.failed_to_delete_user ||
        (lang === "ar" ? "فشل حذف المستخدم" : "Failed to delete user")
      message.error(
        getStoreApiErrorMessage(error?.response?.data, lang, { generic: fallback }),
      )
    } finally {
      setDeletingUserId(null)
    }
  }

  // ✨ NEW: Handle view user details - Update URL
  const handleViewUser = (userId) => {
    setSelectedUserId(userId)
    setShowUserDashboard(true)
    // Update URL with userId query param
    setSearchParams({ userId: userId.toString() })
  }

  // ✨ NEW: Handle back to table - Clear URL
  const handleBackToTable = () => {
    setShowUserDashboard(false)
    setSelectedUserId(null)
    // Remove userId from URL
    setSearchParams({})
  }

  // ✨ If showing user dashboard, render it instead of table
  if (showUserDashboard) {
    return <UserDashboard userId={selectedUserId} onBack={handleBackToTable} />
  }

  // 🧾 Table Columns
  const columns = [
    {
      title: nowrapHeader(lang === "ar" ? "الطلب من" : "Request By"),
      dataIndex: "name",
      key: "name",
      width: 220,
      fixed: "left",
      render: (text, record) => (
        <Space>
          <Avatar
            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
              text
            )}&background=random`}
          />
          <div>
            <div style={{ fontWeight: 500 }}>{text}</div>
            <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
              {record.email}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: nowrapHeader(lang === "ar" ? "رقم الهاتف" : "Phone Number"),
      dataIndex: "phone",
      key: "phone",
      width: 150,
      render: (phone) => (
        <span style={{ color: "#4a5565", fontSize: "13px", whiteSpace: "nowrap" }}>
          {phone}
        </span>
      ),
    },
    {
      title: renderSortableTitle(
        lang === "ar" ? "البلد" : "Country",
        "country"
      ),
      dataIndex: "country",
      key: "country",
      width: 150,
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      render: (text, record) => (
        <Space>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 2px rgba(0,0,0,0.15)",
              background: "#f0f0f0",
              flexShrink: 0,
            }}
          >
            {record.countryCode ? (
              <img
                src={`https://flagcdn.com/w40/${record.countryCode.toLowerCase()}.png`}
                alt={record.countryCode}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <span style={{ fontSize: "14px", color: "#888" }}>🏳️</span>
            )}
          </div>
          <span style={{ whiteSpace: "nowrap" }}>{text}</span>
        </Space>
      ),
    },
    {
      title: nowrapHeader(lang === "ar" ? "الباقة الحالية" : "Current Plan"),
      dataIndex: "plan",
      key: "plan",
      width: 140,
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
      render: (plan, record) => {
        let style = {}

        // ✅ Normalize Arabic to English for consistent color mapping
        const normalizedPlan =
          plan === "فليكس" || plan === "Flex"
            ? "Flex"
            : plan === "التجربة المجانية" || plan === "Free Trial"
            ? "Free Trial"
            : plan === "سجنتر" || plan === "Signature"
            ? "Signature"
            : plan === "ادفانس" || plan === "Advanced"
            ? "Advanced"
            : plan === "بريميوم" || plan === "Premium"
            ? "Premium"
            : plan

        switch (normalizedPlan) {
          case "Flex":
            style = {
              color: "#0A379A",
              backgroundColor: "#EAF1FF",
              borderColor: "#8EB2FF",
            }
            break
          case "Signature":
            style = {
              color: "#774BCF",
              backgroundColor: "#F4F0FF",
              borderColor: "#D9D6FE",
            }
            break
          case "Advanced":
            style = {
              color: "#B93815",
              backgroundColor: "#FEF6EE",
              borderColor: "#F9DBAF",
            }
            break
          case "Premium":
            style = {
              color: "#C11574",
              backgroundColor: "#FDF2FA",
              borderColor: "#FCCEEE",
            }
            break
          case "Free":
          case "Free Trial":
            style = {
              color: "#101828",
              backgroundColor: "#F9FAFB",
              borderColor: "#E5E7EB",
            }
            break
          default:
            style = {
              color: "#344054",
              backgroundColor: "#F9FAFB",
              borderColor: "#E5E7EB",
            }
            break
        }

        // Payment method + card brand shown under the plan tag, e.g. "Card • Visa".
        const PAYMENT_LABELS = {
          CARD: lang === "ar" ? "بطاقة" : "Card",
          APPLEPAY: "Apple Pay",
          TAMARA: lang === "ar" ? "تمارا" : "Tamara",
        }
        const CARD_TYPE_LABELS = {
          VISA: "Visa",
          MASTER: "Mastercard",
          MASTERCARD: "Mastercard",
          MADA: "mada",
          AMEX: "Amex",
        }
        const pm = record?.paymentMethod
        const pmLabel = pm ? PAYMENT_LABELS[pm] || pm : null
        const ct = record?.cardType
        const ctLabel = ct
          ? CARD_TYPE_LABELS[String(ct).toUpperCase()] || ct
          : null
        const paymentLine = [pmLabel, ctLabel].filter(Boolean).join(" • ")

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 4,
            }}
          >
            <Tag style={style}>{plan || "—"}</Tag>
            {paymentLine && hasActivePlan(plan) ? (
              <span style={{ fontSize: 11, color: "#8C8C80" }}>
                {paymentLine}
              </span>
            ) : null}
          </div>
        )
      },
    },

    {
      title: renderSortableTitle(
        lang === "ar" ? "عدد تسجيلات الدخول" : "Login Count",
        "loginCount"
      ),
      dataIndex: "loginCount",
      key: "loginCount",
      width: 140,
      align: "center",
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      render: (count) => (
        <span style={{ fontWeight: 600, color: "#308AA3" }}>{count}</span>
      ),
    },
    {
      width: 140,
      title: renderSortableTitle(
        lang === "ar" ? "الاشتراكات" : "Subscriptions",
        "subscriptions"
      ),
      dataIndex: "subscriptions",
      key: "subscriptions",
      align: "center",
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
    },
    {
      width: 190,
      title: renderSortableTitle(
        lang === "ar" ? "تاريخ آخر اشتراك" : "Last Subscription",
        "lastSubscriptionDate"
      ),
      dataIndex: "lastSubscriptionDate",
      key: "lastSubscriptionDate",
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      render: (value, record) => {
        const date = formatDateOnly(value)
        if (!date) return <span style={{ color: "#98A2B3" }}>—</span>
        const isRenewal = record?.lastSubscriptionIsRenewal === true
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 4,
            }}
          >
            <span style={{ whiteSpace: "nowrap" }}>{date}</span>
            <Tag
              style={
                isRenewal
                  ? {
                      color: "#026AA2",
                      backgroundColor: "#F0F9FF",
                      borderColor: "#B9E6FE",
                    }
                  : {
                      color: "#027A48",
                      backgroundColor: "#ECFDF3",
                      borderColor: "#ABEFC6",
                    }
              }
            >
              {isRenewal
                ? lang === "ar"
                  ? "تجديد"
                  : "Renewal"
                : lang === "ar"
                  ? "أول اشتراك"
                  : "First"}
            </Tag>
          </div>
        )
      },
    },
    {
      width: 130,
      title: renderSortableTitle(
        lang === "ar" ? "النفقات" : "Spendings",
        "spendings"
      ),
      dataIndex: "spendings",
      key: "spendings",
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      render: (value) => (
        <span style={{ whiteSpace: "nowrap" }}>{value}</span>
      ),
    },
    {
      width: 130,
      title: renderSortableTitle(
        lang === "ar" ? "مدفوع من زوري" : "Zory Paid",
        "zoryPaid"
      ),
      dataIndex: "zory_paid",
      key: "zory_paid",
      align: "center",
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      render: (zoryPaid) => {
        if (zoryPaid == null) return <span style={{ color: "#98A2B3" }}>—</span>
        const yes = zoryPaid === true
        return (
          <span
            style={{
              whiteSpace: "nowrap",
              fontWeight: 600,
              color: yes ? "#3538CD" : "#344054",
            }}
          >
            {yes ? (lang === "ar" ? "نعم" : "Yes") : lang === "ar" ? "لا" : "No"}
          </span>
        )
      },
    },
    {
      width: 140,
      title: renderSortableTitle(
        lang === "ar" ? "آخر نشاط" : "Last Activity",
        "lastActivity"
      ),
      dataIndex: "lastActivity",
      key: "lastActivity",
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      render: (value) => (
        <span style={{ whiteSpace: "nowrap" }}>{value}</span>
      ),
    },
    {
      title: renderSortableTitle(
        lang === "ar" ? "تاريخ الإنشاء" : "Created On",
        "createdOn"
      ),
      dataIndex: "createdOn",
      key: "createdOn",
      width: 130,
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      render: (value) => (
        <span style={{ whiteSpace: "nowrap" }}>{value}</span>
      ),
    },
    {
      title: nowrapHeader(lang === "ar" ? "الإجراءات" : "Actions"),
      key: "actions",
      width: 100,
      align: "center",
      fixed: "right",
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      render: (_, record) => {
        const items = [
          {
            key: "view",
            label: lang === "ar" ? "عرض" : "View",
            icon: <EyeOutlined />,
          },
          {
            key: "update_plan",
            label: lang === "ar" ? "تحديث الباقة" : "Update Plan",
            icon: <PlusOutlined />,
          },
          ...(canDeleteUser
            ? [
                {
                  key: "delete",
                  label: lang === "ar" ? "حذف" : "Delete",
                  icon: (
                    <img src="/store/trash.svg" alt="" width={16} height={16} />
                  ),
                  danger: true,
                },
              ]
            : []),
        ]

        return (
          <Dropdown
            trigger={["click"]}
            menu={{
              items,
              onClick: ({ key }) => {
                if (key === "view") handleViewUser(record.userId)
                if (key === "delete" && canDeleteUser)
                  handleOpenDeleteModal(record)
                if (key === "update_plan") handleOpenUpdatePlanModal(record)
              },
            }}
          >
            <button
              type="button"
              aria-label={lang === "ar" ? "المزيد" : "More actions"}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
                padding: 0,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: "#101828",
              }}
            >
              <MoreOutlined style={{ fontSize: 18 }} />
            </button>
          </Dropdown>
        )
      },
    },
  ]

  return (
    <div style={{ paddingTop: "20px", background: "#f5f5f5" }}>
      <div
        style={{ background: "white", borderRadius: "8px", padding: "24px" }}
      >
        {/* 🔍 Search + Filters */}
        <div
          style={{
            marginBottom: "24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* 🔍 Search */}
          <Input
            placeholder={
              lang === "ar"
                ? "ابحث بالاسم أو البريد أو الهاتف"
                : "Search by name, email, or phone"
            }
            prefix={
              <SearchOutlined style={{ color: "#667085", fontSize: "16px" }} />
            }
            style={{
              width: "300px",
              height: "36px",
              backgroundColor: "#F9FAFB",
              color: "#4A5565",
              border: "1px solid #E5E7EB",
              boxShadow: "0px 1px 0.5px 0.05px #1D293D05",
              borderRadius: "8px",
              fontSize: "14px",
              paddingLeft: "10px",
            }}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setPagination((prev) => ({ ...prev, current: 1 }))
            }}
          />

          {/* 🔽 Filters */}
          <Space>
            <Select
              className="user-table-select plan-select-multiple"
              mode="multiple"
              placeholder={lang === "ar" ? "كل الباقات" : "All Plans"}
              value={filters.plan}
              style={{
                width: 180,
                height: "36px",
                backgroundColor: "#F9FAFB",
                borderRadius: "8px",
              }}
              onChange={handlePlanFilterChange}
              dropdownStyle={{
                borderRadius: "8px",
              }}
              maxTagCount="responsive"
              showSearch={false}
              popupClassName="plan-select-dropdown"
            >
              <Option value="plan">
                {lang === "ar" ? "كل الباقات" : "All Plans"}
              </Option>
              <Option value="all_paid_plans">
                {lang === "ar" ? "الباقات المدفوعة" : "Paid Plans"}
              </Option>
              <Option 
                value="Free Trial"
                disabled={filters.plan.includes("all_paid_plans")}
              >
                {lang === "ar" ? "التجربة المجانية" : "Free Trial"}
              </Option>
              <Option 
                value="Signature"
                disabled={filters.plan.includes("all_paid_plans")}
              >
                {lang === "ar" ? "سجنتر" : "Signature"}
              </Option>
              <Option 
                value="Advanced"
                disabled={filters.plan.includes("all_paid_plans")}
              >
                {lang === "ar" ? "ادفانس" : "Advanced"}
              </Option>
              <Option 
                value="Premium"
                disabled={filters.plan.includes("all_paid_plans")}
              >
                {lang === "ar" ? "بريميوم" : "Premium"}
              </Option>
              <Option 
                value="Flex"
                disabled={filters.plan.includes("all_paid_plans")}
              >
                {lang === "ar" ? "فليكس" : "Flex"}
              </Option>
            </Select>

            <Select
              className="user-table-select"
              value={filters.status}
              style={{
                width: 180,
                height: "36px",
                backgroundColor: "#F9FAFB",
                borderRadius: "8px",
              }}
              dropdownStyle={{
                borderRadius: "8px",
              }}
              onChange={(val) => handleFilterChange("status", val)}
            >
              <Option value="all">
                {lang === "ar" ? "كل الحالات" : "All Status"}
              </Option>
              <Option value="active">
                {lang === "ar" ? "اشتراك نشط" : "Active Subscription"}
              </Option>
              <Option value="inactive">
                {lang === "ar" ? "اشتراك غير نشط" : "Inactive Subscription"}
              </Option>
              <Option value="has_subscription">
                {lang === "ar" ? "جميع الاشتراكات" : "All Subscriptions"}
              </Option>
              <Option value="no_subscription">
                {lang === "ar" ? "بدون اشتراك" : "No Subscription"}
              </Option>
              <Option value="zory-paid">
                {lang === "ar" ? "مدفوع من زوري" : "Zory Paid"}
              </Option>
            </Select>

            <Select
              className="user-table-select"
              defaultValue="country"
              style={{
                width: 180,
                height: "36px",
                backgroundColor: "#F9FAFB",
                borderRadius: "8px",
              }}
              dropdownStyle={{
                borderRadius: "8px",
              }}
              onChange={(val) => handleFilterChange("country", val)}
              loading={loading}
            >
              <Option value="country">
                {lang === "ar" ? "كل البلدان" : "All Countries"}
              </Option>
              {availableCountries.length > 0 ? (
                availableCountries.map((code) => (
                  <Option key={code} value={code}>
                    <Space>
                      <div
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#f0f0f0",
                        }}
                      >
                        <img
                          src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
                          alt={code}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                      <span>{getCountryName(code)}</span>
                    </Space>
                  </Option>
                ))
              ) : (
                <Option disabled>
                  {lang === "ar" ? "لا توجد بلدان" : "No countries found"}
                </Option>
              )}
            </Select>

            <Dropdown
              overlayStyle={{
                borderRadius: "8px",
                overflow: "hidden",
                background: "#F2F4F7",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
              menu={{
                items: [
                  {
                    key: "csv",
                    label: (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "14px",
                          color: "#101828",
                        }}
                      >
                        <DownloadOutlined style={{ fontSize: 16 }} />
                        {lang === "ar" ? "ملف CSV" : "CSV File"}
                      </div>
                    ),
                  },
                ],
                onClick: (e) => {
                  if (e.key === "csv") {
                    handleExportCSV()
                  }
                },
              }}
              trigger={["click"]}
            >
              <Button
                type="primary"
                style={{
                  backgroundColor: "#3E3A36",
                  color: "#fff",
                  borderRadius: "8px",
                  height: "36px",
                  fontWeight: 500,
                  padding: "0 20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                }}
              >
                {lang === "ar" ? "تصدير" : "Export"}
                <DownOutlined style={{ fontSize: 12 }} />
              </Button>
            </Dropdown>

            {canCreateUser ? (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateUserModalOpen(true)}
                style={{
                  backgroundColor: "#504943",
                  borderColor: "#504943",
                  borderRadius: "8px",
                  height: "36px",
                  fontWeight: 500,
                }}
              >
                {allUsersT.btn_create_user}
              </Button>
            ) : null}
          </Space>
        </div>

        {/* 🧾 Table */}
        <div className="responsive-table-wrapper user-management-table-wrap">
          <Spin spinning={loading}>
            <ConfigProvider direction={lang === "ar" ? "rtl" : "ltr"}>
              <Table
                className="user-management-table"
                columns={columns}
                dataSource={data}
                onChange={handleTableChange}
                pagination={{
                  current: pagination.current,
                  total: pagination.total,
                  pageSize: pagination.pageSize,
                  showSizeChanger: false,
                }}
                scroll={{ x: 1660, y: 500 }}
              />
            </ConfigProvider>
          </Spin>
        </div>
      </div>

      <DeleteConfirmationModal
        deleteLoading={Boolean(deletingUserId)}
        visible={Boolean(userToDelete)}
        onCancel={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title={modal?.confirm_delete_text}
        content={
          <>
            {modal?.description_1} <b>{userToDelete?.name}</b>?{" "}
            {modal?.description_2}
          </>
        }
      />

      <CreateDashboardUserModal
        open={createUserModalOpen}
        onClose={() => setCreateUserModalOpen(false)}
        onSuccess={() => {
          setPagination((prev) => ({ ...prev, current: 1 }))
          fetchUsers(1)
        }}
      />

      <UpdateDashboardUserPlanModal
        open={updatePlanModalOpen}
        onClose={handleCloseUpdatePlanModal}
        userId={updatePlanUser?.userId}
        userRecord={updatePlanUser}
        onSuccess={() => fetchUsers(pagination.current)}
      />
    </div>
  )
}

export default UserManagmentTable
